import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { VisitEntity } from '_entities';
import { findErrorDetail } from 'utils/custom';
import { notification } from 'utils/services';

const { detachVisitFromCampaign, updateVisit } = VisitEntity.actions;

// A visit can belong to a campaign in two ways, and each one detaches differently:
//  - secondary link (VisitCampaignLink) -> DELETE /visits/:visitId/campaign-links/:campaignId
//  - primary assignment                 -> PATCH  /visits/:visitId with assignment: null
// The backend clears the visit's campaign FK (and the matching link) on the PATCH branch.
const getErrorMessage = (err) => {
  let detail = findErrorDetail(err?.response?.data);
  if (typeof detail === 'string') {
    try {
      detail = JSON.parse(detail);
    } catch (_) {
      // keep original string
    }
  }
  return Array.isArray(detail) ? detail.join(', ') : detail || 'Something went wrong during the request';
};

/**
 * Shared un-attach logic for the Manage Assignments tables and the Visit detail header,
 * so both places hit the same endpoints and show the same success/error toasts.
 *
 * detachVisit({ visitId, campaignId, isLinked }) resolves to `true` on success and
 * `false` when the request failed (the error toast is already shown by then).
 *
 * detachVisitFromAll({ visitId, campaigns }) un-attaches the visit from every campaign
 * it belongs to and resolves to `{ detached, failed }` so the caller can refresh even
 * after a partial detach. Each campaign entry is `{ id, isLinked }`.
 */
const useDetachVisitFromCampaign = () => {
  const dispatch = useDispatch();
  const [isDetaching, setIsDetaching] = useState(false);

  const requestDetach = useCallback(
    ({ visitId, campaignId, isLinked }) =>
      isLinked
        ? dispatch(detachVisitFromCampaign(visitId, campaignId))
        : dispatch(
            updateVisit(
              { data: { relationships: { assignment: { data: null } }, id: visitId, type: 'Visit' } },
              visitId,
            ),
          ),
    [dispatch],
  );

  const detachVisit = useCallback(
    async ({ visitId, campaignId, isLinked = false }) => {
      setIsDetaching(true);
      try {
        await requestDetach({ visitId, campaignId, isLinked });
        notification.success(`Visit (${visitId}) was successfully un-attached!`);
        return true;
      } catch (err) {
        notification.error(` ${getErrorMessage(err)}`);
        return false;
      } finally {
        setIsDetaching(false);
      }
    },
    [requestDetach],
  );

  const detachVisitFromAll = useCallback(
    async ({ visitId, campaigns = [] }) => {
      setIsDetaching(true);
      // Secondary links first, primary last: detaching the primary also drops the link
      // row for that campaign on the backend, so this order keeps every request valid.
      const ordered = [...campaigns.filter((c) => c.isLinked), ...campaigns.filter((c) => !c.isLinked)];
      let detached = 0;
      let firstError = null;

      try {
        // Sequential on purpose — the requests touch the same visit and assignment rows.
        await ordered.reduce(
          (chain, campaign) =>
            chain.then(async () => {
              if (firstError) return;
              try {
                await requestDetach({ visitId, campaignId: campaign.id, isLinked: campaign.isLinked });
                detached += 1;
              } catch (err) {
                firstError = err;
              }
            }),
          Promise.resolve(),
        );

        if (firstError) {
          notification.error(` ${getErrorMessage(firstError)}`);
        } else {
          notification.success(
            `Visit (${visitId}) was successfully un-attached from ${detached} campaign${detached === 1 ? '' : 's'}!`,
          );
        }
      } finally {
        setIsDetaching(false);
      }

      return { detached, failed: ordered.length - detached };
    },
    [requestDetach],
  );

  return { detachVisit, detachVisitFromAll, isDetaching };
};

export default useDetachVisitFromCampaign;
